import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { generateSecureRandomString } from '../../shared/utils/random-utils';

export interface ToastMessage {
  id: string;
  message: string;
  type: 'success' | 'error';
  position?: 'top-right' | 'bottom-center';
  duration?: number;
}

@Injectable({
  providedIn: 'root'
})
export class FormToasterService {
  private readonly toastsSubject = new BehaviorSubject<ToastMessage[]>([]);
  public toasts$: Observable<ToastMessage[]> = this.toastsSubject.asObservable();
  
  // Track recently shown messages to prevent duplicates
  private readonly recentMessages: Map<string, number> = new Map();
  private readonly DEDUPLICATION_WINDOW = 2000; // 2 seconds

  /**
   * Show a success toast message
   */
  showSuccess(message: string, position: 'top-right' | 'bottom-center' = 'top-right', duration: number = 5000): void {
    this.show(message, 'success', position, duration);
  }

  /**
   * Show an error toast message
   */
  showError(message: string, position: 'top-right' | 'bottom-center' = 'top-right', duration: number = 5000): void {
    this.show(message, 'error', position, duration);
  }

  /**
   * Show a toast message
   */
  private show(message: string, type: 'success' | 'error', position: 'top-right' | 'bottom-center' = 'top-right', duration: number = 5000): void {
    // Create a unique key for deduplication (message + type + position)
    const messageKey = `${message}|${type}|${position}`;
    const now = Date.now();
    
    // Check if the same message was shown recently
    const lastShownTime = this.recentMessages.get(messageKey);
    if (lastShownTime && (now - lastShownTime) < this.DEDUPLICATION_WINDOW) {
      // Also check if a toast with the same message already exists in current toasts
      const currentToasts = this.toastsSubject.value;
      const duplicateExists = currentToasts.some(
        toast => toast.message === message && toast.type === type && toast.position === position
      );
      
      if (duplicateExists) {
        // Don't show duplicate toast
        return;
      }
    }
    
    // Update the recent messages map
    this.recentMessages.set(messageKey, now);
    
    // Clean up old entries from the map (older than deduplication window)
    this.cleanupRecentMessages(now);

    // Clear existing toasts to show only one toaster at a time
    this.toastsSubject.next([]);

    const toast: ToastMessage = {
      id: this.generateId(),
      message,
      type,
      position,
      duration
    };

    // Show only the new toast
    this.toastsSubject.next([toast]);

    // Auto-remove after specified duration (default 5 seconds)
    setTimeout(() => {
      this.remove(toast.id);
    }, duration);
  }
  
  /**
   * Clean up old entries from recentMessages map
   */
  private cleanupRecentMessages(now: number): void {
    for (const [key, timestamp] of this.recentMessages.entries()) {
      if (now - timestamp >= this.DEDUPLICATION_WINDOW) {
        this.recentMessages.delete(key);
      }
    }
  }

  /**
   * Remove a toast by ID
   */
  remove(id: string): void {
    const currentToasts = this.toastsSubject.value;
    this.toastsSubject.next(currentToasts.filter(toast => toast.id !== id));
  }

  /**
   * Clear all toasts
   */
  clear(): void {
    this.toastsSubject.next([]);
  }

  /**
   * Generate a unique ID for toast
   */
  private generateId(): string {
    return `toast-${Date.now()}-${generateSecureRandomString(9)}`;
  }
}

